import { inject, Injectable } from '@angular/core';
import { isNumber } from 'es-toolkit/compat';
import { EMPTY, Observable, switchMap, take } from 'rxjs';

import { StoryWithInsightDto } from '@malou-io/package-dto';
import { CsvAsStringArrays, getPlatformKeysWithStories, PlatformFilterPage } from '@malou-io/package-utils';

import { StoriesApiService } from '#modules/posts/services/stories.api.service';
import { RestaurantStore } from '#modules/restaurants/stores/restaurant.store';
import {
    AbstractCsvService,
    DEFAULT_CSV_PAGINATION,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

@Injectable()
export class StoryCsvInsightsService extends AbstractCsvService<StoryWithInsightDto[]> {
    private readonly _restaurantStore = inject(RestaurantStore);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _storiesService = inject(StoriesApiService);
    private readonly _statisticsStore = inject(StatisticsStore);

    protected _isDataValid(data: StoryWithInsightDto[]): boolean {
        return data.length > 0;
    }

    protected override _getData$(): Observable<StoryWithInsightDto[]> {
        const restaurantId = this._restaurantStore.currentRestaurantId();
        const { startDate, endDate } = this._statisticsStore.filters().dates;
        const platformKeys = getPlatformKeysWithStories().filter((platformKey) =>
            this._statisticsStore.getPlatformsFiltersByPage(PlatformFilterPage.SOCIAL_NETWORKS).includes(platformKey)
        );

        if (!startDate || !endDate || platformKeys.length === 0) {
            return EMPTY;
        }

        return this._storiesService.synchronizeStories$(restaurantId).pipe(
            take(1),
            switchMap(() =>
                this._storiesService.getStoriesWithInsights$(restaurantId, {
                    platformKeys,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    pagination: { ...DEFAULT_CSV_PAGINATION, skip: 0 },
                })
            )
        );
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return ['Date', 'Hour', 'Platform', 'Impressions', 'Reach', 'Exits', 'Next story', 'Previous story'];
    }

    protected override _getCsvDataRows(stories: StoryWithInsightDto[]): CsvAsStringArrays {
        return stories.map((story) => {
            const createdAt = new Date(story.createdAt);
            const date = createdAt.toLocaleDateString();
            const hour = `${createdAt.getHours()}:${createdAt.getMinutes()}`;
            const platform = this._enumTranslatePipe.transform(story.key, 'platform_key');

            return [
                date,
                hour,
                platform,
                this._formatMetric(story.impressions),
                this._formatMetric(story.reach),
                this._formatMetric(story.tapsExits),
                this._formatMetric(story.tapsForward),
                this._formatMetric(story.tapsBack),
            ];
        });
    }

    private _formatMetric(value: number | null): string {
        if (!isNumber(value)) {
            return 'N/A';
        }
        return value < 0 ? '< 5' : value.toString();
    }
}
